import { Stack } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { formatEnumCase } from '@/utils/formatting/strings';

import {
  FeatureFlagRow,
  formatEnablementStatus,
  formatTeamLabel,
  GroupByField,
} from './AdminFeatureFlagsPage.utils';

interface FeatureFlagsTableProps {
  rows: FeatureFlagRow[];
  loading: boolean;
  groupBy: GroupByField;
  onRowClick: (row: FeatureFlagRow) => void;
}

function useFeatureFlagColumns(
  groupBy: GroupByField
): Column<FeatureFlagRow>[] {
  return useMemo(
    () =>
      (
        [
          {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            minWidth: 200,
            renderCell: TwoLineTextRenderer({
              lineOne: ({ row }) => row.name,
              lineOneProps: { variant: 'title-xs' },
              lineTwo: ({ row }) => row.description ?? undefined,
            }),
          },
          {
            field: 'owner',
            headerName: 'Owner',
            flex: 1,
            minWidth: 120,
            renderCell: TextRenderer({
              text: ({ row }) => row.owner || '—',
            }),
          },
          {
            field: 'team',
            headerName: 'Team',
            flex: 1,
            minWidth: 120,
            renderCell: TextRenderer({
              text: ({ row }) => (row.team ? formatTeamLabel(row.team) : '—'),
            }),
          },
          {
            field: 'group',
            headerName: 'Group',
            flex: 1,
            minWidth: 100,
            renderCell: TextRenderer({
              text: ({ row }) => (row.group ? formatEnumCase(row.group) : '—'),
            }),
          },
          {
            field: 'enablementStatus',
            headerName: 'Status',
            flex: 1,
            minWidth: 160,
            renderCell: TextRenderer({
              text: ({ row }) => formatEnablementStatus(row.enablementStatus),
            }),
          },
          {
            field: 'onReleaseGroups',
            headerName: 'Release groups',
            sortable: false,
            flex: 1.5,
            minWidth: 220,
            renderCell: ({ row }) => {
              const hasOverrides =
                row.onReleaseGroups.length > 0 ||
                row.offReleaseGroups.length > 0;
              if (!hasOverrides) {
                return <CellContainer align="left">—</CellContainer>;
              }
              return (
                <CellContainer align="left">
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={0.5}
                    alignItems="center"
                  >
                    {row.onReleaseGroups.map((rg) => (
                      <Badge
                        key={`on-${rg.id}`}
                        variant={BadgeVariants.Success}
                        display={formatEnumCase(rg.name)}
                      />
                    ))}
                    {row.offReleaseGroups.map((rg) => (
                      <Badge
                        key={`off-${rg.id}`}
                        variant={BadgeVariants.Destructive}
                        display={`Off - ${formatEnumCase(rg.name)}`}
                      />
                    ))}
                  </Stack>
                </CellContainer>
              );
            },
          },
          {
            field: 'createdAt',
            headerName: 'Created',
            flex: 0.8,
            minWidth: 110,
            renderCell: TextRenderer({
              text: ({ row }) => formatDateToMMDDYY(row.createdAt),
            }),
          },
        ] as Column<FeatureFlagRow>[]
      ).filter((col) => col.field !== groupBy),
    [groupBy]
  );
}

export function FeatureFlagsTable({
  rows,
  loading,
  groupBy,
  onRowClick,
}: FeatureFlagsTableProps) {
  const columns = useFeatureFlagColumns(groupBy);

  const handleRowClick = (params: GridRowParams<FeatureFlagRow>) => {
    onRowClick(params.row);
  };

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={loading}
      pagination={false}
      hideFooterPagination
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: () => <TableEmptyState text="No feature flags" />,
      }}
    />
  );
}
